import React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type {
  PreviewUserRow,
  PreviewUserStatus,
} from "../../../../types/user-master";

const STATUS_ROW_COLORS: Partial<Record<PreviewUserStatus, string>> = {
  add: "#E3F2FD",
  update: "#FFF9C4",
  delete: "#FFEBEE",
  error: "#EEEEEE",
};

const STATUS_LABELS: Partial<Record<PreviewUserStatus, string>> = {
  add: "追加",
  update: "変更",
  delete: "削除",
  error: "エラー",
  ok: "ok",
};

type ColumnId = keyof PreviewUserRow;

type Column = {
  id: ColumnId;
  label: string;
  minWidth: string;
};

const columns: Column[] = [
  { id: "row_no", label: "行番号", minWidth: "80px" },
  { id: "status", label: "ステータス", minWidth: "100px" },
  { id: "name", label: "ユーザ名", minWidth: "160px" },
  { id: "open_id", label: "メールアドレス", minWidth: "200px" },
];

export type PreviewTableProps = {
  users?: PreviewUserRow[];
  users_delete?: PreviewUserRow[];
};

export default function PreviewTable({
  users = [],
  users_delete = [],
}: PreviewTableProps) {
  const rows = [
    ...users.map((row) => ({
      ...row,
      key: `user-${row.row_no}-${row.open_id}`,
    })),
    ...users_delete.map((row) => ({
      ...row,
      key: `delete-${row.row_no}-${row.open_id}`,
    })),
  ].filter((row) => row.status !== "ok");

  if (rows.length === 0) {
    return null;
  }

  const formatStatus = (status: PreviewUserStatus) =>
    STATUS_LABELS[status] ?? status;

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <TableContainer>
        <Table stickyHeader aria-label="preview table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    padding: "8px",
                    fontWeight: "bold",
                    borderBottom: 2,
                    borderBottomColor: "outline.dark",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={row.key}
                data-testid={`previewResult_${index}`}
                sx={{
                  backgroundColor:
                    STATUS_ROW_COLORS[row.status] || "transparent",
                }}
              >
                {columns.map((column) => (
                  <TableCell key={column.id} sx={{ padding: "8px" }}>
                    {column.id === "status"
                      ? formatStatus(row.status)
                      : row[column.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
